import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Home, BookCheck, Award, GraduationCap, MessageCircle, BarChart3,
  BookOpen, Newspaper, ShoppingBag, Heart, User, HelpCircle, LogOut,
  TrendingUp, ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';

const GURU_NAV = [
  {
    section: 'Menu Utama',
    items: [
      { icon: Home,           label: 'Beranda',         path: '/guru'               },
      { icon: BookCheck,      label: 'Input Setoran',   path: '/guru/setoran'       },
      { icon: Award,          label: "Tasmi'",          path: '/guru/tasmi'         },
      { icon: GraduationCap,  label: 'Ujian Tahfidz',   path: '/guru/ujian-tahfidz' },
      { icon: MessageCircle,  label: 'Chat',            path: '/chat'               },
      { icon: BarChart3,      label: 'Rekap Absensi',   path: '/guru/rekap'         },
    ],
  },
  {
    section: 'Lainnya',
    items: [
      { icon: BookOpen,    label: 'Al-Quran',      path: '/quran'  },
      { icon: Newspaper,   label: 'BQ News',       path: '/berita' },
      { icon: ShoppingBag, label: 'Toko',          path: '/toko'   },
      { icon: Heart,       label: 'Donasi',        path: '/donasi' },
    ],
  },
  {
    section: 'Akun',
    items: [
      { icon: User,        label: 'Profil Saya',   path: '/guru/profil' },
      { icon: HelpCircle,  label: 'Pedoman',       path: '/pedoman'     },
    ],
  },
];

const SANTRI_NAV = [
  {
    section: 'Menu Utama',
    items: [
      { icon: Home,           label: 'Beranda',         path: '/santri'               },
      { icon: ClipboardList,  label: 'Riwayat Setoran', path: '/santri/setoran'       },
      { icon: TrendingUp,     label: 'Progres Hafalan', path: '/santri/progress'      },
      { icon: Award,          label: "Tasmi'",          path: '/santri/tasmi'         },
      { icon: GraduationCap,  label: 'Ujian Tahfidz',   path: '/santri/ujian-tahfidz' },
      { icon: MessageCircle,  label: 'Chat',            path: '/chat'                 },
    ],
  },
  {
    section: 'Lainnya',
    items: [
      { icon: BookOpen,    label: 'Al-Quran',      path: '/quran'  },
      { icon: Newspaper,   label: 'BQ News',       path: '/berita' },
      { icon: ShoppingBag, label: 'Toko',          path: '/toko'   },
      { icon: Heart,       label: 'Donasi',        path: '/donasi' },
    ],
  },
  {
    section: 'Akun',
    items: [
      { icon: User,        label: 'Profil Saya',   path: '/santri/profil' },
      { icon: HelpCircle,  label: 'Pedoman',       path: '/pedoman'       },
    ],
  },
];

export default function DesktopSidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const navConfig = user?.role === 'santri' ? SANTRI_NAV : GURU_NAV;

  const handleLogout = async () => {
    await logout();
    toast.success('Berhasil keluar');
    navigate('/welcome');
  };

  const isActive = (path) => {
    if (path === '/guru' || path === '/santri') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <aside
      data-testid="desktop-sidebar"
      className="desktop-sidebar bg-white border-r border-emerald-50"
      style={{ boxShadow: '2px 0 16px rgba(16,185,129,0.06)' }}
    >
      {/* Logo */}
      <div className="px-5 py-4 border-b border-emerald-50 flex items-center gap-3 flex-shrink-0">
        <img src="/logo-zahbaq.png" alt="ZahBaq" className="w-9 h-9 object-contain flex-shrink-0" />
        <div className="min-w-0">
          <h1 className="font-playfair font-bold text-emerald-900 text-base leading-tight">ZahBaq</h1>
          <p className="text-[10px] text-emerald-500 font-medium capitalize">{user?.role === 'santri' ? 'Portal Santri' : 'Portal Guru'}</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-3">
        {navConfig.map((group) => (
          <div key={group.section} className="mb-4">
            <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest px-3 mb-1.5">{group.section}</p>
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <button
                    key={item.path}
                    data-testid={`desktop-nav-${item.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 text-left ${
                      active
                        ? 'bg-emerald-500 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-emerald-50 hover:text-emerald-700'
                    }`}
                  >
                    <Icon size={17} className="flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User info + logout */}
      <div className="flex-shrink-0 px-3 py-3 border-t border-emerald-50">
        <div className="flex items-center gap-2.5 px-2 py-2 mb-1">
          <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <span className="text-emerald-700 font-bold text-sm">
              {user?.name?.charAt(0)?.toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-800 truncate">{user?.name}</p>
            <p className="text-[10px] text-emerald-500 capitalize">{user?.role}</p>
          </div>
        </div>
        <button
          data-testid="desktop-sidebar-logout"
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-red-500 hover:bg-red-50 transition-colors"
        >
          <LogOut size={15} />
          Keluar
        </button>
      </div>
    </aside>
  );
}
